import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { Appointment } from '@/react-query/useAppointments';
import {
  useCreateReferral,
  useReferralDetails,
  useReferralStatus,
  useResubmitReferral,
  useUpdateReferral,
} from '@/react-query/useReferralInfo';
import { useAppContext } from '@/utils/hooks/useAppContext';
interface ResubmitReferralResponse {
  success: boolean;
  create_message: string;
  oralinxId?: string;
}

export const useReferralActions = (
  appointment: Appointment,
  enabled: boolean = true,
) => {
  const { user } = useAppContext();
  const queryClient = useQueryClient();
  const [isOpenReferModal, setIsOpenReferModal] = useState(false);
  const [isOpenSpecialtyModal, setIsOpenSpecialtyModal] = useState(false);

  const { mutate: createReferral } = useCreateReferral();
  const { mutate: updateReferral } = useUpdateReferral(
    appointment.appointment.unifiedId,
  );
  const {
    mutate: resubmitReferral,
    isLoading,
    isSuccess,
    isError,
    data,
    reset,
  } = useResubmitReferral();
  const { data: referralStatus } = useReferralStatus(
    appointment.appointment.unifiedId,
    {
      enabled: enabled && !!appointment.appointment.unifiedId,
    },
  );
  const { data: referralDetails } = useReferralDetails(
    appointment.appointment.unifiedId,
    {
      enabled: enabled && !!appointment.appointment.unifiedId,
    },
  );

  const handleOpenReferModal = async () => {
    if (!enabled) return;

    if (!user?.primaryPracticeId) {
      alert(
        'Cannot create referral: primary practice ID is undefined. Please contact support.',
      );
      return;
    }

    if (!referralStatus || referralStatus.status === 'not_found') {
      try {
        await createReferral({
          practiceId: user.primaryPracticeId,
          unifiedAppointmentId: appointment.appointment.unifiedId,
        });
      } catch (error) {
        alert(
          'Failed to create referral. Please try again or contact support.',
        );
        return;
      }
    }
    setIsOpenReferModal(true);
  };

  const handleFlagReferral = () => {
    if (!enabled) return;

    if (!user?.primaryPracticeId) {
      alert(
        'Cannot flag referral: primary practice ID is undefined. Please contact support.',
      );
      return;
    }

    // Open specialty selection modal
    setIsOpenSpecialtyModal(true);
  };

  const handleSpecialtyConfirm = async (specialtyId: number) => {
    if (!enabled) return;

    if (!user?.primaryPracticeId) {
      alert(
        'Cannot flag referral: primary practice ID is undefined. Please contact support.',
      );
      return;
    }

    try {
      // First create referral if it doesn't exist
      if (!referralStatus || referralStatus.status === 'not_found') {
        await createReferral(
          {
            practiceId: user.primaryPracticeId,
            unifiedAppointmentId: appointment.appointment.unifiedId,
            status: 'needs_referral',
            specialtyId: specialtyId,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries([
                'referral',
                appointment.appointment.unifiedId,
                'status',
              ]);
              setIsOpenSpecialtyModal(false);
            },
            onError: () => {
              alert(
                'Failed to flag referral. Please try again or contact support.',
              );
            },
          },
        );
      } else {
        updateReferral(
          { status: 'needs_referral', specialtyId: specialtyId },
          {
            onSuccess: () => {
              queryClient.invalidateQueries([
                'referral',
                appointment.appointment.unifiedId,
                'status',
              ]);
              setIsOpenSpecialtyModal(false);
            },
            onError: () => {
              alert(
                'Failed to flag referral. Please try again or contact support.',
              );
            },
          },
        );
      }
    } catch (error) {
      alert('Failed to flag referral. Please try again or contact support.');
    }
  };

  const handleUnmarkReferral = () => {
    if (!enabled) return;

    updateReferral(
      { status: 'not_started' },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            'referral',
            appointment.appointment.unifiedId,
            'status',
          ]);
        },
        onError: () => {
          alert(
            'Failed to unmark referral. Please try again or contact support.',
          );
        },
      },
    );
  };

  const handleResubmitReferral = () => {
    if (!enabled || !referralDetails?.id) return;

    resubmitReferral(referralDetails.id, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'referral',
          appointment.appointment.unifiedId,
          'status',
        ]);
        queryClient.invalidateQueries([
          'referral',
          appointment.appointment.unifiedId,
          'details',
        ]);
        queryClient.invalidateQueries(['referrals']);
      },
    });
  };

  return {
    referralStatus: enabled ? referralStatus?.status : undefined,
    hasRequestData: enabled ? referralStatus?.hasRequestData : undefined,
    isOpenReferModal,
    setIsOpenReferModal,
    isOpenSpecialtyModal,
    setIsOpenSpecialtyModal,
    handleOpenReferModal,
    handleFlagReferral,
    handleSpecialtyConfirm,
    handleUnmarkReferral,
    handleResubmitReferral,
    isResubmitting: isLoading,
    isResubmitSuccess: isSuccess,
    isResubmitError: isError,
    resubmitData: data as ResubmitReferralResponse | undefined,
    resetResubmit: reset,
  };
};
